import axios from 'axios';
import ajax from '@client/components/ajax';
import { ApiInfluencerUpsertRequest } from '@client/new/api/types';
import { useSetCustomUrlMutation } from 'generated/graphql';
import { ProfileFormValues } from '../view-model/Profile.schema';

const AT_LEAST_ONE_SOCIAL_REQUIRED_MESSAGE =
  'Add at least one Instagram, TikTok, or YouTube profile before saving.';

export const toSaveProfileErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const code = error.response?.data?.code;
    if (code === 'AT_LEAST_ONE_SOCIAL_REQUIRED') {
      return AT_LEAST_ONE_SOCIAL_REQUIRED_MESSAGE;
    }
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'Some changes could not be saved';
};

type SaveProfileDeps = {
  creatorId: number;
  customURL: string;
  originalCustomURL: string;
};

type ProfileUpsertPayload = Pick<
  ApiInfluencerUpsertRequest,
  | 'id'
  | 'firstName'
  | 'lastName'
  | 'title'
  | 'gender'
  | 'messageProfile'
  | 'accomplishments'
  | 'profileInstagram'
  | 'profileTiktok'
  | 'profileYoutubeChannel'
  | 'keywords'
> & {
  mediaAvatar: Exclude<ProfileFormValues['mediaAvatar'], undefined> | null;
  mediaCover: Exclude<ProfileFormValues['mediaCover'], undefined> | null;
  mediaBanner: Exclude<ProfileFormValues['mediaBanner'], undefined> | null;
  mediaWelcome: Exclude<ProfileFormValues['mediaWelcome'], undefined> | null;
};

// Media fields must serialize as explicit `null` (not `undefined`) when the
// creator deletes an image/video — JSON.stringify drops `undefined`, so the
// backend would otherwise never receive the clear instruction. sc-111242
export const toInfluencerPayload = (
  values: ProfileFormValues,
  creatorId: number,
): ProfileUpsertPayload => ({
  id: creatorId,
  firstName: values.firstName,
  lastName: values.lastName,
  title: values.title,
  gender: values.gender ?? '',
  messageProfile: values.messageProfile,
  accomplishments: values.accomplishments,
  profileInstagram: values.profileInstagram ?? '',
  profileTiktok: values.profileTiktok ?? '',
  profileYoutubeChannel: values.profileYoutubeChannel ?? '',
  mediaAvatar: values.mediaAvatar ?? null,
  mediaCover: values.mediaCover ?? null,
  mediaBanner: values.mediaBanner ?? null,
  mediaWelcome: values.mediaWelcome ?? null,
  keywords: values.keywords,
});

export const useSaveProfileModel = (deps: SaveProfileDeps) => {
  const [setCustomUrlMutation] = useSetCustomUrlMutation({
    variables: {
      customURL: deps.customURL,
    },
  });

  const saveProfile = async (values: ProfileFormValues) => {
    const customUrlChanged = deps.customURL !== deps.originalCustomURL;

    const payload = toInfluencerPayload(values, deps.creatorId);
    const promises: Promise<unknown>[] = [
      ajax.upsertInfluencer(payload as unknown as ApiInfluencerUpsertRequest),
      ajax.upsertCustomColorSchema(deps.creatorId, values.customColorSchema),
    ];

    if (customUrlChanged) {
      promises.push(setCustomUrlMutation());
    }

    const results = await Promise.allSettled(promises);
    const failures = results.filter((r) => r.status === 'rejected');

    if (failures.length > 0) {
      const firstError =
        failures[0].status === 'rejected' ? failures[0].reason : null;
      throw new Error(toSaveProfileErrorMessage(firstError));
    }
  };

  return { saveProfile };
};
